/**
 * R.H.I.E. Daily Ritual Widget
 * "Bugünün Ritüeli" Dashboard Widget
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Play, Sparkles, Moon, Sun, Brain, Clock, ChevronRight, Zap } from 'lucide-react';
import { useRHIE } from './RHIEProvider';

const timeIcons = {
  morning: Sun,
  noon: Brain,
  night: Moon
};

const getTimeOfDay = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'noon';
  return 'night';
};

const timeMessages = {
  morning: 'Günaydın! Bugünün enerjisi seninle.',
  noon: 'Günün ortasında bir nefes al.',
  night: 'Gece ritüeli zamanı. Bilinçaltını programla.'
};

export default function DailyRitualWidget() {
  const navigate = useNavigate();
  const { dailyRitual, currentStreak, todayCompleted, aiSuggestion, startRitual } = useRHIE();
  
  const timeOfDay = getTimeOfDay();
  const TimeIcon = timeIcons[timeOfDay];
  
  const handleStartRitual = () => {
    if (dailyRitual) {
      startRitual(dailyRitual);
      navigate('/panel/telkinler');
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.15), rgba(99, 102, 241, 0.1))',
        border: '1px solid rgba(124, 58, 237, 0.2)'
      }}
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
        <div className="absolute inset-0 bg-gradient-radial from-purple-500/40 to-transparent" />
      </div>
      
      <div className="relative p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Bugünün Ritüeli</h3>
              <p className="text-white/40 text-xs">{timeMessages[timeOfDay]}</p>
            </div>
          </div>
          
          {/* Streak badge */}
          {currentStreak > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/20">
              <Sparkles className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-purple-400 text-xs font-medium">{currentStreak}</span>
            </div>
          )}
        </div>
        
        {/* Ritual Card */}
        {dailyRitual ? (
          <div 
            className="relative rounded-xl p-4 cursor-pointer group"
            style={{ background: 'rgba(255,255,255,0.03)' }}
            onClick={handleStartRitual}
          >
            <div className="flex items-center gap-4">
              {/* Icon */}
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.4), rgba(124, 58, 237, 0.2))',
                  boxShadow: '0 8px 25px -10px rgba(124, 58, 237, 0.5)'
                }}
              >
                <TimeIcon className="w-6 h-6 text-purple-300" />
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-medium truncate mb-1">
                  {dailyRitual.title}
                </h4>
                <div className="flex items-center gap-3 text-xs text-white/40">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {dailyRitual.duration_display || '10 dk'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Zap className="w-3 h-3 text-amber-400" />
                    +{dailyRitual.base_xp || 15} ZP
                  </span>
                </div>
              </div>
              
              {/* Play button */}
              {!todayCompleted ? (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                  style={{ 
                    background: 'linear-gradient(135deg, #7C3AED, #9F7AEA)',
                    boxShadow: '0 4px 20px -5px rgba(124, 58, 237, 0.5)'
                  }}
                >
                  <Play className="w-5 h-5 text-white ml-0.5" />
                </motion.button>
              ) : (
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-green-400 text-lg">✓</span>
                </div>
              )}
            </div>
            
            {/* AI suggestion reason */}
            {aiSuggestion?.reason && (
              <div className="mt-3 pt-3 border-t border-white/5">
                <p className="text-white/30 text-xs flex items-center gap-1">
                  <Brain className="w-3 h-3" />
                  {aiSuggestion.reason}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-white/40 text-sm">Ritüelin hazırlanıyor...</p>
          </div>
        )}
        
        {/* Today's status */}
        {todayCompleted && (
          <div className="mt-4 flex items-center justify-center gap-2 py-2 rounded-lg bg-green-500/10">
            <span className="text-green-400 text-sm">✓ Bugün dönüşümüne bir adım daha ekledin</span>
          </div>
        )}
        
        {/* View all link */}
        <button
          onClick={() => navigate('/panel/telkinler')}
          className="w-full mt-4 flex items-center justify-center gap-1 py-2 text-purple-400 text-sm hover:text-purple-300 transition-colors"
        >
          Tüm telkinleri keşfet
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}
